// =============================================================================
// Pôster A3 de oferta — um produto por página.
//
// A gerente escolhe os produtos da campanha, e cada um vira um pôster no modelo
// dos que ela faz à mão (Black Friday 2026): arte do topo, foto do produto,
// folha do preço e o período no rodapé. Dá pra arrastar a foto e a folha, mudar
// o tamanho dos dois, trocar a foto (que vira sugestão pro cadastro do comercial)
// e a arte da campanha.
//
// A prévia usa o MESMO CSS do PDF (vem do servidor) e o mesmo ajuste da folha
// (helpers/posterFolha.js), então o que aparece aqui é o que sai impresso.
// =============================================================================
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import { API } from '../contexts/AuthContext';
import { prepararFoto, reduzirParaEnvio, estiloPadrao, ESTILOS, prepararArte } from '../helpers/fotoCatalogo';
import { arrumarFolhaPoster, arrumarPeriodo, limitarFolha } from '../helpers/posterFolha';
import AvisoSugestaoFoto from '../components/AvisoSugestaoFoto';
import AvisoRecorteIA from '../components/AvisoRecorteIA';
import { esquecerFoto } from '../helpers/recorteIA';
import { useUsoPecas, estiloLinhaUsada, MarcasOutrasPecas, LegendaUso } from '../components/UsoPecas';
import PreenchimentoAuto from '../components/PreenchimentoAuto';
import AvisoIdentidade from '../components/estudio/AvisoIdentidade';

const LADO_FOTO = 1400;          // foto do pôster: impressa grande, mais resolução que no catálogo
const PROPORCAO = 420 / 297;     // A3 em pé
const A3_POL = 297 / 25.4;       // largura do A3 em polegadas (pra dar os dpi da arte)
const POR_PDF = 60;              // máximo de pôsteres por PDF no servidor (routes/poster.js)
const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const uid = () => Math.random().toString(36).slice(2, 10);
const moeda = (v) => Number(v || 0).toFixed(2).replace('.', ',');
const milhar = (s) => String(s).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const moedaMilhar = (v) => { const [i, c] = moeda(v).split(','); return `${milhar(i)},${c}`; };
const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
// Onde a folha fica de fato: dentro da página e sem cobrir o período (mesma conta do PDF).
const folhaNaPagina = (folha, p, modelo, periodo) => limitarFolha(folha,
  { prop: modelo.folhaProp, mostrarDe: !!p.mostrarDe && p.de > p.por, comPeriodo: !!periodo });

// ---- O produto do pôster é editado IGUAL ao do story (CampanhaStory.js › EditorStory):
// a caixa do produto inteiro (foto + folha + fita do "De:") leva o contorno azul e
// os cantos; clicar no preço pega só o preço; a fita do "De:" anda sozinha.
const r1 = (v) => Math.round(v * 10) / 10;
const DE_PADRAO = { x: 20, y: -13 };                  // fita do "De:" dentro da folha (% dela) = CSS .p-de
const altFolhaPct = (w, folhaProp) => w / folhaProp / PROPORCAO;   // altura da folha em % da altura da página
function caixaProduto(p, modelo, periodo) {
  const fl = folhaNaPagina(p.folha, p, modelo, periodo);
  const fh = altFolhaPct(fl.w, modelo.folhaProp);
  let x0 = Math.min(p.area.x, fl.x), y0 = Math.min(p.area.y, fl.y);
  let x1 = Math.max(p.area.x + p.area.w, fl.x + fl.w), y1 = Math.max(p.area.y + p.area.h, fl.y + fh);
  if (p.mostrarDe && p.de > p.por) {
    const d = p.posDe || DE_PADRAO;
    const tx = fl.x + d.x / 100 * fl.w, ty = fl.y + d.y / 100 * fh;   // a fita mede ~metade da folha × 19% dela
    x0 = Math.min(x0, tx); y0 = Math.min(y0, ty); x1 = Math.max(x1, tx + fl.w * 0.5); y1 = Math.max(y1, ty + fh * 0.19);
  }
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
// Produto inteiro maior/menor em volta de um ponto fixo (ax, ay em % da página).
function escalarPoster(q, k, ax, ay, modelo, periodo) {
  const fl = folhaNaPagina(q.folha, q, modelo, periodo);
  const kk = limitar(k, Math.max(5 / q.area.w, 15 / fl.w), Math.min(150 / q.area.w, 95 / fl.w));
  const px = (x) => r1(ax + (x - ax) * kk), py = (y) => r1(ay + (y - ay) * kk);
  return { area: { ...q.area, x: px(q.area.x), y: py(q.area.y), w: r1(q.area.w * kk), h: r1(q.area.h * kk) },
    folha: folhaNaPagina({ x: px(fl.x), y: py(fl.y), w: r1(fl.w * kk) }, q, modelo, periodo) };
}
// Anda o produto inteiro; se a folha bate no limite da página, a foto para junto (não descola).
function moverPoster(q, dx, dy, modelo, periodo) {
  const fl = folhaNaPagina(q.folha, q, modelo, periodo);
  const nf = folhaNaPagina({ ...fl, x: fl.x + dx, y: fl.y + dy }, q, modelo, periodo);
  return { area: { ...q.area, x: r1(q.area.x + nf.x - fl.x), y: r1(q.area.y + nf.y - fl.y) }, folha: { ...nf, x: r1(nf.x), y: r1(nf.y) } };
}

const fotoPadrao = (p) => ({ origem: p?.fotoPropria ? 'propria' : 'erp', estilo: estiloPadrao(p?.classe),
  tocouEstilo: false, url: null, bruta: null, carregando: true, ver: 0, cobre: false });

// Posição de foto e folha do modelo escolhido (cópia, pra poder mexer).
const posicoesDoModelo = (modelos, nome) => {
  const m = modelos[nome];
  return { modelo: nome, area: { ...m.area }, folha: { ...m.folha } };
};

const novoPoster = (p, modelos, modeloPadrao) => ({
  id: uid(), codpro: p.codpro, nome: p.nome, de: p.de, por: p.por, classe: p.classe,
  fotoPropria: p.fotoPropria, sugestao: p.sugestao,
  ...posicoesDoModelo(modelos, modeloPadrao), mostrarDe: false, foto: fotoPadrao(p),
});

const paraSalvar = (p) => ({ codpro: p.codpro, nome: p.nome, modelo: p.modelo, area: p.area, folha: p.folha, posDe: p.posDe || null,
  mostrarDe: p.mostrarDe, foto: { origem: p.foto.origem, estilo: p.foto.estilo, tocouEstilo: p.foto.tocouEstilo } });

// `embutido`: aba do Estúdio de Criação (pages/EstudioCampanha.js) — sem o
// cabeçalho próprio, a arte da campanha fica na aba Identidade (`versaoArte` muda
// quando ela troca) e a tela conta pra aba quantos pôsteres tem e se há mudança
// não salva (`onResumo`).
export default function CampanhaPoster({ embutido = false, campanhaId, versaoArte = 0, onResumo, onIdentidade } = {}) {
  const { id: idRota } = useParams();
  const id = String(campanhaId || idRota);
  const navigate = useNavigate();
  const [campanha, setCampanha] = useState(null);
  const [modelo, setModelo] = useState(null);         // css, modelos, info das artes (GET /poster/.../modelo)
  const [artes, setArtes] = useState(null);           // { topo, fundo } em object URL — o que a prévia desenha
  const [disponiveis, setDisponiveis] = useState([]);
  const [posters, setPosters] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [periodo, setPeriodo] = useState('');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [fontes, setFontes] = useState(0);             // muda quando as fontes terminam de carregar
  const [auto, setAuto] = useState(false);             // janela do preenchimento automático
  const uso = useUsoPecas(id);
  const montado = useRef(true);
  const urlsBrutas = useRef(new Set());

  // Fontes (Poppins e a do período) chegando depois mudam a largura do texto:
  // a folha precisa ser reajustada.
  useEffect(() => {
    let vivo = true;
    document.fonts?.ready.then(() => { if (vivo) setFontes(n => n + 1); });
    const aoCarregar = () => setFontes(n => n + 1);
    document.fonts?.addEventListener?.('loadingdone', aoCarregar);
    return () => { vivo = false; document.fonts?.removeEventListener?.('loadingdone', aoCarregar); };
  }, []);

  // As artes vêm como imagem (com o token) e viram object URL: o fundo em alta
  // passa de 10 MB e não cabe embutido no JSON. Com o fundo completo, o topo sai.
  const carregarModelo = useCallback(async () => {
    const r = await axios.get(`${API}/poster/campanha/${id}/modelo`);
    const baixar = async (tipo) => URL.createObjectURL((await axios.get(`${API}/poster/campanha/${id}/arte/${tipo}`, { responseType: 'blob' })).data);
    const completo = r.data.artes.completo ? await baixar('completo') : null;
    const novas = completo ? { fundo: completo, topo: null } : { fundo: await baixar('fundo'), topo: await baixar('topo') };
    setArtes(ant => {
      if (ant) Object.values(ant).forEach(u => u && URL.revokeObjectURL(u));
      return novas;
    });
    setModelo(r.data);
    return r.data;
  }, [id]);
  const artesAtuais = useRef(null);
  useEffect(() => { artesAtuais.current = artes; }, [artes]);
  useEffect(() => () => { Object.values(artesAtuais.current || {}).forEach(u => u && URL.revokeObjectURL(u)); }, []);
  // A identidade mudou na aba ao lado: redesenha com a arte nova.
  const primeiraArte = useRef(true);
  useEffect(() => {
    if (primeiraArte.current) { primeiraArte.current = false; return; }
    carregarModelo().catch(() => {});
  }, [versaoArte, carregarModelo]);

  // ---- Carga inicial
  useEffect(() => {
    (async () => {
      try {
        const [mod, p, c, l] = await Promise.all([
          carregarModelo(),
          axios.get(`${API}/catalogo/campanha/${id}/produtos`),
          axios.get(`${API}/campanhas/${id}`),
          axios.get(`${API}/poster/campanha/${id}/layout`),
        ]);
        const prods = p.data.produtos || [];
        setDisponiveis(prods);
        setCampanha(c.data);
        const salvo = l.data?.layout;
        setPeriodo(salvo?.periodo ?? mod.periodoPadrao ?? '');
        if (salvo?.posters?.length) {
          const porCod = new Map(prods.map(x => [x.codpro, x]));
          const lista = salvo.posters.map(s => {
            const prod = porCod.get(s.codpro);
            if (!prod) return null;   // saiu da campanha ou perdeu a aprovação
            const base = novoPoster(prod, mod.modelos, mod.modeloPadrao);
            const pos = mod.modelos[s.modelo] ? posicoesDoModelo(mod.modelos, s.modelo) : {};
            return { ...base, ...pos, nome: s.nome || base.nome, mostrarDe: !!s.mostrarDe, posDe: s.posDe || null,
              area: { ...base.area, ...pos.area, ...s.area }, folha: { ...base.folha, ...pos.folha, ...s.folha },
              foto: { ...base.foto, ...s.foto, url: null, bruta: null, carregando: true } };
          }).filter(Boolean);
          setPosters(lista);
          setSelecionado(lista[0]?.id || null);
        }
      } catch (e) {
        Swal.fire('Erro', e.response?.data?.error || 'Não consegui carregar a campanha.', 'error');
      } finally {
        setCarregando(false);
      }
    })();
  }, [id, carregarModelo]);

  // ---- Fotos: mesma receita do catálogo (busca com o token, trata no navegador)
  const buscarFoto = useCallback(async (codpro, origem) => {
    const url = origem === 'propria'
      ? `${API}/catalogo/campanha/${id}/foto/${codpro}`
      : `${API}/produtos/${codpro}/imagem/1`;
    const r = await axios.get(url, { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    urlsBrutas.current.add(u);
    return u;
  }, [id]);
  const soltarUrl = (u) => { if (u) { URL.revokeObjectURL(u); urlsBrutas.current.delete(u); esquecerFoto(u); } };

  const tratarFoto = useCallback(async (p) => {
    try {
      const bruta = p.foto.bruta || await buscarFoto(p.codpro, p.foto.origem);
      let estilo = p.foto.estilo || 'recorte';
      let r = await prepararFoto(bruta, { estilo, ladoMax: LADO_FOTO });
      if (r.sugestao && !p.foto.tocouEstilo) {
        estilo = r.sugestao;
        r = await prepararFoto(bruta, { estilo, ladoMax: LADO_FOTO });
      }
      return { ...p.foto, bruta, url: r.url, cobre: r.cobre, estilo, carregando: false, sincronizada: false };
    } catch {
      return { ...p.foto, url: null, carregando: false, erro: true };
    }
  }, [buscarFoto]);

  const mudarPoster = useCallback((pid, fn) => setPosters(prev => prev.map(p => (p.id === pid ? fn(p) : p))), []);

  const mudarFoto = useCallback((pid, mudanca) => {
    mudarPoster(pid, p => {
      if ('bruta' in mudanca && p.foto.bruta) soltarUrl(p.foto.bruta);
      return { ...p, foto: { ...p.foto, ...mudanca, carregando: true, erro: false, sincronizada: false, ver: (p.foto.ver || 0) + 1 } };
    });
  }, [mudarPoster]);

  // Fila: duas fotos por vez; resultado de versão velha é descartado.
  const emAndamento = useRef(new Set());
  const [fimDeTarefa, setFimDeTarefa] = useState(0);
  useEffect(() => {
    const livres = 2 - emAndamento.current.size;
    if (livres <= 0) return;
    posters
      .filter(p => p.foto.carregando && !p.foto.erro && !emAndamento.current.has(`${p.id}:${p.foto.ver}`))
      .slice(0, livres)
      .forEach(p => {
        const chave = `${p.id}:${p.foto.ver}`;
        emAndamento.current.add(chave);
        tratarFoto(p).then(foto => {
          emAndamento.current.delete(chave);
          if (!montado.current) return;
          setPosters(prev => prev.map(x => (x.id === p.id && x.foto.ver === p.foto.ver ? { ...x, foto: { ...foto, ver: p.foto.ver } } : x)));
          setFimDeTarefa(n => n + 1);
        });
      });
  }, [posters, tratarFoto, fimDeTarefa]);

  useEffect(() => {
    const urls = urlsBrutas.current;
    montado.current = true;
    return () => {
      montado.current = false;
      urls.forEach(u => { URL.revokeObjectURL(u); esquecerFoto(u); });
      urls.clear();
    };
  }, []);

  // ---- Montagem
  const noPoster = useMemo(() => new Set(posters.map(p => p.codpro)), [posters]);
  const lista = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return disponiveis.filter(p => !t || p.nome.toLowerCase().includes(t) || p.codpro.includes(t));
  }, [disponiveis, busca]);

  const adicionar = (prod) => {
    if (noPoster.has(prod.codpro)) {
      setSelecionado(posters.find(p => p.codpro === prod.codpro)?.id);
      return;
    }
    const novo = novoPoster(prod, modelo.modelos, modelo.modeloPadrao);
    setPosters(prev => [...prev, novo]);
    setSelecionado(novo.id);
  };
  // Preenchimento automático: um pôster por produto, do que mais vende pro que
  // menos vende, alternando os modelos (ou no modelo escolhido).
  const gerarAutomatico = (cfg) => {
    const nomes = Object.keys(modelo.modelos);
    const base = cfg.substituir ? [] : posters;
    const ja = new Set(base.map(p => p.codpro));
    const novos = cfg.produtos.filter(prod => !ja.has(prod.codpro)).map((prod, k) => {
      const nome = cfg.modelo === 'alternar' ? nomes[k % nomes.length] : (modelo.modelos[cfg.modelo] ? cfg.modelo : modelo.modeloPadrao);
      return { ...novoPoster(prod, modelo.modelos, nome), mostrarDe: cfg.mostrarDe && prod.de > prod.por };
    });
    if (cfg.substituir) posters.forEach(p => { if (p.foto.bruta) soltarUrl(p.foto.bruta); });
    setPosters([...base, ...novos]);
    setSelecionado(novos[0]?.id || base[0]?.id || null);
    setAuto(false);
  };

  const remover = (pid) => {
    const alvo = posters.find(p => p.id === pid);
    if (alvo?.foto.bruta) soltarUrl(alvo.foto.bruta);
    const resto = posters.filter(p => p.id !== pid);
    setPosters(resto);
    if (selecionado === pid) setSelecionado(resto[0]?.id || null);
  };
  const mover = (pid, d) => setPosters(prev => {
    const i = prev.findIndex(p => p.id === pid), j = i + d;
    if (i < 0 || j < 0 || j >= prev.length) return prev;
    const nova = [...prev];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    return nova;
  });

  // Mudança não salva = o que vai no Salvar, menos o estilo que o recorte
  // sugeriu sozinho (só conta o escolhido à mão).
  const assinatura = useMemo(() => JSON.stringify({ periodo, p: posters.map(p => {
    const x = paraSalvar(p);
    return { ...x, foto: { origem: x.foto.origem, estilo: x.foto.tocouEstilo ? x.foto.estilo : '' } };
  }) }), [posters, periodo]);
  const [salvo, setSalvo] = useState(null);
  useEffect(() => { if (!carregando && salvo === null) setSalvo(assinatura); }, [carregando, salvo, assinatura]);
  const sujo = salvo !== null && assinatura !== salvo;
  useEffect(() => { onResumo?.({ qtd: posters.length, sujo }); }, [onResumo, posters.length, sujo]);

  const salvar = async () => {
    setSalvando(true);
    const assinando = assinatura;
    try {
      await axios.put(`${API}/poster/campanha/${id}/layout`, { versao: 1, periodo, posters: posters.map(paraSalvar) });
      setSalvo(assinando);
      Swal.fire({ icon: 'success', title: 'Pôsteres salvos', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || 'Não consegui salvar.', 'error');
    } finally { setSalvando(false); }
  };

  // Fotos tratadas sobem uma por vez antes de gerar (todas juntas estouram o limite do pedido).
  const sincronizarFotos = async (alvos) => {
    const pendentes = alvos.filter(p => p.foto.url && !p.foto.sincronizada);
    for (const p of pendentes) {
      await axios.post(`${API}/poster/campanha/${id}/pronta/${p.codpro}`, { dados: p.foto.url })
        .catch(err => { throw new Error(`Foto do Cód.${p.codpro}: ${err.response?.data?.error || err.message}`); });
    }
    if (pendentes.length) {
      const enviadas = new Map(pendentes.map(p => [p.id, p.foto.url]));
      setPosters(prev => prev.map(p => (enviadas.get(p.id) === p.foto.url ? { ...p, foto: { ...p.foto, sincronizada: true } } : p)));
    }
  };

  const gerar = async ({ soEste = null } = {}) => {
    const alvos = soEste ? posters.filter(p => p.id === soEste) : posters;
    if (!alvos.length) return Swal.fire('Atenção', 'Monte pelo menos um pôster.', 'info');
    const tratando = alvos.filter(p => p.foto.carregando && !p.foto.erro).length;
    if (tratando) {
      return Swal.fire({ icon: 'info', title: 'Fotos ainda sendo tratadas',
        text: `Faltam ${tratando} foto(s). Espere terminar e gere de novo.` });
    }
    const semFoto = alvos.filter(p => !p.foto.url);
    if (semFoto.length && !soEste) {
      const r = await Swal.fire({ icon: 'warning', title: 'Antes de gerar',
        html: `<div style="text-align:left;font-size:0.9rem"><b>${semFoto.length} pôster(es) sem foto</b>: ${semFoto.map(p => `Cód.${p.codpro}`).join(', ')}</div>`,
        showCancelButton: true, confirmButtonText: 'Gerar assim', cancelButtonText: 'Voltar' });
      if (!r.isConfirmed) return;
    }
    setGerando(true);
    Swal.fire({ title: soEste ? 'Gerando a prévia...' : `Gerando ${alvos.length} pôster(es)...`, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      await sincronizarFotos(alvos);
      // O servidor faz até POR_PDF pôsteres por arquivo: mais que isso sai em partes.
      const partes = [];
      for (let k = 0; k < alvos.length; k += POR_PDF) partes.push(alvos.slice(k, k + POR_PDF));
      const nome = `Poster A3 - ${(campanha?.nome || 'campanha').replace(/[\\/:*?"<>|]/g, '-')}`;
      for (let k = 0; k < partes.length; k++) {
        if (partes.length > 1) { const t = Swal.getTitle(); if (t) t.textContent = `Gerando a parte ${k + 1} de ${partes.length}...`; }
        const r = await axios.post(`${API}/poster/campanha/${id}/pdf`, {
          periodo,
          // Preço o servidor pega do banco; `comFoto` diz se o pôster tem foto agora.
          posters: partes[k].map(p => ({ codpro: p.codpro, nome: p.nome, modelo: p.modelo, area: p.area, folha: p.folha,
            posDe: p.posDe || null, mostrarDe: p.mostrarDe, preencher: p.foto.cobre, comFoto: !!p.foto.url })),
        }, { responseType: 'blob' });
        const url = URL.createObjectURL(r.data);
        if (soEste) {
          setPrevia(ant => { if (ant) URL.revokeObjectURL(ant); return url; });
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = partes.length > 1 ? `${nome} (${k + 1} de ${partes.length}).pdf` : `${nome}.pdf`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
        }
      }
      Swal.close();
    } catch (e) {
      let msg = 'Não consegui gerar o pôster.';
      const dados = e.response?.data;
      try {
        const corpo = dados instanceof Blob ? JSON.parse(await dados.text()) : dados;
        if (corpo?.error) msg = corpo.error;
      } catch { /* resposta sem JSON */ }
      if (!e.response) msg = e.message?.startsWith('Foto do Cód.') ? e.message : `Sem resposta do servidor (${e.message}).`;
      Swal.fire('Erro', msg, 'error');
    } finally { setGerando(false); }
  };

  // ---- Arte própria da campanha (topo / fundo / fundo completo)
  const trocarArte = async (tipo, file) => {
    if (!file) return;
    Swal.fire({ title: 'Enviando a arte...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const arte = await prepararArte(file);
      await axios.post(`${API}/poster/campanha/${id}/arte/${tipo}`, arte);
      await carregarModelo();
      Swal.close();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || e.message || 'Não consegui trocar a arte.', 'error');
    }
  };
  const voltarArte = async (tipo) => {
    try { await axios.delete(`${API}/poster/campanha/${id}/arte/${tipo}`); await carregarModelo(); }
    catch (e) { Swal.fire('Erro', e.response?.data?.error || 'Não consegui voltar pra arte padrão.', 'error'); }
  };

  if (carregando || !modelo || !artes) {
    return <div className="p-4"><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</div>;
  }

  const atual = posters.find(p => p.id === selecionado) || null;

  return (
    <div style={embutido ? { display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0 }
      : { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', minHeight: 560 }}>
      {/* O CSS do pôster é o mesmo do PDF (fontes e folha embutidas). */}
      <style>{modelo.css}</style>

      <div className={embutido ? 'barra-c' : 'd-flex justify-content-between align-items-center flex-wrap gap-2 mb-2'}>
        <div>
          {!embutido && <>
            <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => navigate(`/campanhas/${id}/produtos`)}>
              <i className="bi bi-arrow-left"></i>
            </button>
            <span className="fw-bold" style={{ fontSize: '1.1rem' }}>Pôster A3 — {campanha?.nome}</span>
          </>}
          <span className={embutido ? 'info' : 'text-muted ms-2'} style={{ fontSize: '0.85rem' }}>
            {posters.length} pôster(es){sujo && <span className="ms-2" style={{ color: '#A8460A' }}>• não salvo</span>}
          </span>
          <AvisoRecorteIA />
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
          <label className="text-muted" style={{ fontSize: '0.82rem' }}>Período</label>
          <input className="form-control form-control-sm" style={{ width: 210 }} value={periodo}
            onChange={e => setPeriodo(e.target.value.toUpperCase())} placeholder="01 A 30 DE NOVEMBRO" />
          <button className="btn btn-sm btn-outline-secondary" onClick={salvar} disabled={salvando}>
            <i className="bi bi-save me-1"></i>{salvando ? 'Salvando...' : 'Salvar'}
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={() => gerar({ soEste: atual?.id })} disabled={gerando || !atual}>
            <i className="bi bi-eye me-1"></i>Ver prévia
          </button>
          <button className={`btn btn-sm ${embutido ? 'btn-acao-e' : 'btn-success'}`} onClick={() => gerar()} disabled={gerando || !posters.length}>
            <i className="bi bi-download me-1"></i>Baixar PDF ({posters.length})
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 12 }}>
        {/* ---------------- Produtos da campanha ---------------- */}
        <div className="card p-2" style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <input className="form-control form-control-sm mb-2" placeholder="Buscar produto..."
            value={busca} onChange={e => setBusca(e.target.value)} />
          <small className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>Clique no + pra fazer o pôster do produto.</small>
          <LegendaUso peca="poster" />
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {lista.length === 0 && <div className="text-muted p-2" style={{ fontSize: '0.84rem' }}>Nenhum produto aprovado na campanha.</div>}
            {lista.map(p => (
              <div key={p.codpro} className="d-flex align-items-center gap-2 p-1 border-bottom"
                style={estiloLinhaUsada(noPoster.has(p.codpro), atual?.codpro === p.codpro)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="fw-semibold text-truncate" style={{ fontSize: '0.8rem' }} title={p.nome}>{p.nome}</div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                    Cód.{p.codpro} · R$ {fmt(p.por)}
                    {!p.temFoto && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.6rem' }}>sem foto</span>}
                    <MarcasOutrasPecas uso={uso} codpro={p.codpro} peca="poster" />
                  </div>
                </div>
                {noPoster.has(p.codpro)
                  ? <button className="btn btn-sm btn-link p-0 text-secondary" title="Já tem pôster — abrir" onClick={() => adicionar(p)}>
                      <i className="bi bi-check2-square"></i></button>
                  : <button className="btn btn-sm btn-link text-success p-0" title="Fazer o pôster" onClick={() => adicionar(p)}>
                      <i className="bi bi-plus-lg"></i></button>}
              </div>
            ))}
          </div>
        </div>

        {/* ---------------- Pôsteres montados ---------------- */}
        <div style={{ width: 132, flexShrink: 0, overflowY: 'auto', paddingRight: 2 }}>
          <button className="btn btn-sm btn-warning w-100 mb-2" style={{ fontSize: '0.74rem' }} onClick={() => setAuto(true)}
            title="Monta os pôsteres de uma vez, do que mais vende pro que menos vende">
            <i className="bi bi-magic me-1"></i>Automático
          </button>
          {posters.length === 0 && <div className="text-muted text-center p-2" style={{ fontSize: '0.78rem' }}>Nenhum pôster ainda.</div>}
          {posters.map((p, i) => (
            <div key={p.id} className="mb-2" style={{ cursor: 'pointer' }} onClick={() => setSelecionado(p.id)}>
              <div style={{ outline: p.id === selecionado ? '3px solid #F16A0D' : '1px solid #ccc', borderRadius: 3 }}>
                <Poster p={p} modelo={modelo} artes={artes} periodo={periodo} largura={120} fontes={fontes} />
              </div>
              <div className="d-flex align-items-center justify-content-between" style={{ fontSize: '0.68rem' }}>
                <span className="text-truncate" title={p.nome}>{i + 1}. Cód.{p.codpro}</span>
                <span className="d-flex">
                  <button className="btn btn-link btn-sm p-0 text-secondary" disabled={i === 0} title="Subir"
                    onClick={e => { e.stopPropagation(); mover(p.id, -1); }}><i className="bi bi-chevron-up"></i></button>
                  <button className="btn btn-link btn-sm p-0 text-secondary" disabled={i === posters.length - 1} title="Descer"
                    onClick={e => { e.stopPropagation(); mover(p.id, 1); }}><i className="bi bi-chevron-down"></i></button>
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ---------------- Pôster aberto ---------------- */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto' }}>
          {atual
            ? <EditorPoster p={atual} modelo={modelo} artes={artes} periodo={periodo} fontes={fontes}
                onMudar={(fn) => mudarPoster(atual.id, fn)} />
            : <div className="text-muted mt-5 text-center">
                <i className="bi bi-file-earmark-image d-block mb-2" style={{ fontSize: '2.5rem' }}></i>
                Escolha um produto na lista pra montar o pôster.
              </div>}
        </div>

        {/* ---------------- Ajustes ---------------- */}
        <div className="card p-2" style={{ width: 290, flexShrink: 0, overflowY: 'auto' }}>
          {atual
            ? <Ajustes p={atual} modelo={modelo} campanhaId={id} periodo={periodo}
                onMudar={(fn) => mudarPoster(atual.id, fn)}
                onFoto={(m) => mudarFoto(atual.id, m)}
                onRemover={() => remover(atual.id)} />
            : <small className="text-muted">Os ajustes do pôster aparecem aqui.</small>}
          <hr className="my-2" />
          {embutido
            ? <AvisoIdentidade onIdentidade={onIdentidade} />
            : <ArteCampanha modelo={modelo} onTrocar={trocarArte} onVoltar={voltarArte} />}
        </div>
      </div>

      {auto && (
        <PreenchimentoAuto tipo="poster" campanhaId={id} produtos={disponiveis} jaUsados={noPoster}
          temItens={posters.length > 0} onGerar={gerarAutomatico} onFechar={() => setAuto(false)} />
      )}

      {previa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000, display: 'flex', flexDirection: 'column', padding: 16 }}
          onClick={() => { URL.revokeObjectURL(previa); setPrevia(null); }}>
          <div className="d-flex justify-content-between align-items-center mb-2" style={{ color: '#fff' }} onClick={e => e.stopPropagation()}>
            <span className="fw-bold"><i className="bi bi-file-earmark-pdf me-2"></i>Prévia do pôster</span>
            <button className="btn btn-sm btn-light" onClick={() => { URL.revokeObjectURL(previa); setPrevia(null); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <iframe title="Prévia do pôster" src={previa} onClick={e => e.stopPropagation()}
            style={{ flex: 1, width: '100%', border: 'none', borderRadius: 8, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// O pôster em si, na escala pedida. Mesma estrutura do HTML do PDF
// (backend/src/services/posterPdf.js › posterHTML) e mesmo CSS.
// -----------------------------------------------------------------------------
function Poster({ p, modelo, artes, periodo, largura, fontes, children, onApertar }) {
  const folha = useRef(null);
  const per = useRef(null);
  const fl = folhaNaPagina(p.folha, p, modelo, periodo);
  const altFolha = fl.w / modelo.folhaProp;   // % da largura da página
  const [inteiro, cent] = moeda(p.por).split(',');

  // O ajuste mede o texto já desenhado: roda depois de cada mudança que mexe em largura.
  useLayoutEffect(() => {
    if (folha.current) arrumarFolhaPoster(folha.current);
    if (per.current) arrumarPeriodo(per.current);
  }, [p.nome, p.por, p.folha.w, p.mostrarDe, p.posDe?.x, p.posDe?.y, largura, fontes, periodo]);
  const ap = (alvo) => (onApertar ? (e) => onApertar(e, alvo) : undefined);
  const mao = onApertar ? 'move' : undefined;

  return (
    <div className="poster" style={{ width: largura, height: largura * PROPORCAO, '--pw': `${largura / 100}px`, userSelect: 'none' }}>
      <img className="p-fundo" src={artes.fundo} alt="" draggable={false} />
      {artes.topo && <img className="p-topo" src={artes.topo} alt="" draggable={false} />}
      <div className="p-area" onPointerDown={ap({ tipo: 'produto' })}
        style={{ left: `${p.area.x}%`, top: `${p.area.y}%`, width: `${p.area.w}%`, height: `${p.area.h}%`, cursor: mao }}>
        {p.foto.url
          ? <img className={p.foto.cobre ? 'preenche' : ''} src={p.foto.url} alt="" draggable={false}
              style={{ objectPosition: p.area.alinhar }} />
          : <div className="p-sem-foto">{p.foto.carregando ? <span className="spinner-border spinner-border-sm" /> : 'sem foto'}</div>}
      </div>
      <div ref={folha} className="p-folha" onPointerDown={ap({ tipo: 'folha' })}
        style={{ left: `${fl.x}%`, top: `${fl.y}%`, width: `${fl.w}%`,
          height: `calc(var(--pw) * ${altFolha})`, fontSize: `calc(var(--pw) * ${altFolha})`, cursor: mao }}>
        {p.mostrarDe && p.de > p.por && (
          <span className="p-de" onPointerDown={ap({ tipo: 'de' })}
            style={{ ...(p.posDe ? { left: `${p.posDe.x}%`, top: `${p.posDe.y}%` } : {}), cursor: mao }}>De: R$ {moedaMilhar(p.de)}</span>
        )}
        <span className="p-cod">Cód.{p.codpro}</span>
        <span className="p-nome">{p.nome}</span>
        <span className="p-rs">R$</span>
        <span className="p-int">{milhar(inteiro)}</span>
        <span className="p-cent">,{cent}</span>
        <span className="p-avista">à vista</span>
      </div>
      {periodo && (
        <div className="p-periodo"><img src={modelo.calendario} alt="" draggable={false} /><span ref={per}>{periodo}</span></div>
      )}
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Pôster aberto: o produto é editado IGUAL ao do story. Clica na foto: seleciona
// o produto (caixa azul com foto + preço + "De:"), arrasta tudo e os cantos
// aumentam/diminuem com o canto oposto parado. Clica no preço: pega só o preço
// (caixa laranja, cantos dele; a pílula "preço" faz o mesmo). A fita do "De:"
// anda sozinha. Guias laranja no meio e nas margens; setas ajustam fino.
// -----------------------------------------------------------------------------
function EditorPoster({ p, modelo, artes, periodo, fontes, onMudar }) {
  const caixa = useRef(null);
  const palco = useRef(null);
  const [largura, setLargura] = useState(420);
  const [ativo, setAtivo] = useState(null);       // tipo do arrasto em andamento
  const [sel, setSel] = useState(null);           // null | 'produto' | 'folha'
  const [linhas, setLinhas] = useState([]);       // guias de alinhamento ({ v: x } | { h: y }, em %)
  const arrasto = useRef(null);
  const altura = largura * PROPORCAO;
  const folhaProp = modelo.folhaProp;
  useEffect(() => { setSel(null); }, [p.id]);     // outro pôster aberto

  useEffect(() => {
    const medir = () => {
      const el = caixa.current?.parentElement;
      if (!el) return;
      const porAltura = (el.clientHeight - 30) / PROPORCAO;
      setLargura(Math.max(280, Math.min(el.clientWidth - 24, porAltura)));
    };
    medir();
    const obs = new ResizeObserver(medir);
    if (caixa.current?.parentElement) obs.observe(caixa.current.parentElement);
    return () => obs.disconnect();
  }, []);

  const fl = folhaNaPagina(p.folha, p, modelo, periodo);
  const caixaFolha = { x: fl.x, y: fl.y, w: fl.w, h: altFolhaPct(fl.w, folhaProp) };
  const caixaSel = caixaProduto(p, modelo, periodo);
  const naTela = (rect, x, y) => [rect.left + x / 100 * largura, rect.top + y / 100 * altura];

  const apertar = (e, alvo) => {
    if (!alvo) { setSel(null); return; }   // apertou o fundo: tira a seleção
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    let ancora = null;
    // Puxando um canto, o canto oposto fica parado.
    const oposto = (c) => [alvo.canto.includes('o') ? c.x + c.w : c.x, alvo.canto.includes('n') ? c.y + c.h : c.y];
    if (alvo.tipo === 'escala') ancora = oposto(caixaSel);
    else if (alvo.tipo === 'folha-escala') ancora = oposto(caixaFolha);
    arrasto.current = { alvo, mx: e.clientX, my: e.clientY, base: p, rect: palco.current.getBoundingClientRect(), ancora };
    setSel(alvo.tipo === 'folha' || alvo.tipo === 'folha-escala' ? 'folha' : 'produto');
    setAtivo(alvo.tipo);
  };

  const mover = (e) => {
    const a = arrasto.current;
    if (!a) return;
    const dx = (e.clientX - a.mx) / largura * 100, dy = (e.clientY - a.my) / altura * 100;
    const { alvo, base: q0 } = a;
    const escala = () => {
      const [ax, ay] = naTela(a.rect, a.ancora[0], a.ancora[1]);
      const v0 = [a.mx - ax, a.my - ay], v = [e.clientX - ax, e.clientY - ay];
      const n0 = v0[0] * v0[0] + v0[1] * v0[1];
      return n0 ? Math.max(0.05, (v[0] * v0[0] + v[1] * v0[1]) / n0) : 1;
    };
    // Guias: encosta no meio e nas margens da página (e o preço também na foto).
    const tolX = 6 / largura * 100, tolY = 6 / altura * 100;
    const novas = [];
    const encostar = (pontos, alvos, tol) => {
      let melhor = null;
      pontos.forEach(pt => alvos.forEach(t => { const d = t - pt; if (Math.abs(d) <= tol && (!melhor || Math.abs(d) < Math.abs(melhor.d))) melhor = { d, t }; }));
      return melhor;
    };
    const encaixar = (c, cs) => {
      const sx = encostar([c.x + dx, c.x + c.w + dx, c.x + c.w / 2 + dx], [50, 5, 95, ...cs.flatMap(o => [o.x, o.x + o.w, o.x + o.w / 2])], tolX);
      const sy = encostar([c.y + dy, c.y + c.h + dy, c.y + c.h / 2 + dy], [50, ...cs.flatMap(o => [o.y, o.y + o.h, o.y + o.h / 2])], tolY);
      novas.push(...[sx && { v: sx.t }, sy && { h: sy.t }].filter(Boolean));
      return [dx + (sx?.d || 0), dy + (sy?.d || 0)];
    };
    const f0 = folhaNaPagina(q0.folha, q0, modelo, periodo);
    const h0 = altFolhaPct(f0.w, folhaProp);

    if (alvo.tipo === 'produto') {
      const [ex, ey] = encaixar(caixaProduto(q0, modelo, periodo), []);
      const n = moverPoster(q0, ex, ey, modelo, periodo);
      onMudar(q => ({ ...q, area: n.area, folha: n.folha }));
    } else if (alvo.tipo === 'folha') {
      const [ex, ey] = encaixar({ x: f0.x, y: f0.y, w: f0.w, h: h0 }, [q0.area]);
      const nf = folhaNaPagina({ ...f0, x: f0.x + ex, y: f0.y + ey }, q0, modelo, periodo);
      onMudar(q => ({ ...q, folha: { ...nf, x: r1(nf.x), y: r1(nf.y) } }));
    } else if (alvo.tipo === 'folha-escala') {
      const w = limitar(f0.w * escala(), 15, 95), h = altFolhaPct(w, folhaProp);
      const [ax, ay] = a.ancora;
      const nf = folhaNaPagina({ w: r1(w), x: alvo.canto.includes('o') ? ax - w : ax, y: alvo.canto.includes('n') ? ay - h : ay }, q0, modelo, periodo);
      onMudar(q => ({ ...q, folha: { ...nf, x: r1(nf.x), y: r1(nf.y) } }));
    } else if (alvo.tipo === 'de') {
      // Fita do "De:" solta: posição dentro da folha, em % dela.
      const d0 = q0.posDe || DE_PADRAO;
      const fw = f0.w / 100 * largura, fh = h0 / 100 * altura;
      const posDe = { x: r1(limitar(d0.x + (e.clientX - a.mx) / fw * 100, -30, 85)), y: r1(limitar(d0.y + (e.clientY - a.my) / fh * 100, -60, 95)) };
      onMudar(q => ({ ...q, posDe }));
    } else if (alvo.tipo === 'escala') {
      const n = escalarPoster(q0, escala(), a.ancora[0], a.ancora[1], modelo, periodo);
      onMudar(q => ({ ...q, area: n.area, folha: n.folha }));
    }
    setLinhas(novas);
  };
  const soltar = () => { arrasto.current = null; setAtivo(null); setLinhas([]); };

  // Setas: ajuste fino do produto selecionado (Shift anda mais). Só com a tela à vista.
  useEffect(() => {
    if (!sel) return undefined;
    const tecla = (e) => {
      if (!caixa.current?.offsetParent || /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      if (!d) return;
      e.preventDefault();
      const passo = e.shiftKey ? 2 : 0.3;
      onMudar(q => {
        if (sel === 'folha') return { ...q, folha: folhaNaPagina({ ...q.folha, x: q.folha.x + d[0] * passo, y: q.folha.y + d[1] * passo }, q, modelo, periodo) };
        const n = moverPoster(q, d[0] * passo, d[1] * passo, modelo, periodo);
        return { ...q, area: n.area, folha: n.folha };
      });
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [sel, onMudar, modelo, periodo]);

  const contorno = (c, cor) => (
    <div style={{ position: 'absolute', left: `${c.x}%`, top: `${c.y}%`, width: `${c.w}%`, height: `${c.h}%`,
      outline: `2px dashed ${cor}`, outlineOffset: 2, pointerEvents: 'none', zIndex: 20 }} />
  );
  const cantos = (c, tipo) => [['no', c.x, c.y], ['ne', c.x + c.w, c.y], ['so', c.x, c.y + c.h], ['se', c.x + c.w, c.y + c.h]]
    .map(([canto, x, y]) => <AlcaPoster key={canto} canto={canto} x={x} y={y} onPointerDown={(e) => apertar(e, { tipo, canto })} />);

  return (
    <div ref={caixa} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar} style={{ touchAction: 'none', paddingTop: 4 }}>
      <div ref={palco} onPointerDown={(e) => apertar(e, null)}
        style={{ position: 'relative', width: largura, height: altura, boxShadow: '0 4px 18px rgba(0,0,0,0.25)', cursor: ativo ? 'grabbing' : 'default' }}>
        <Poster p={p} modelo={modelo} artes={artes} periodo={periodo} largura={largura} fontes={fontes} onApertar={apertar} />
        {linhas.map((g, i) => (
          <div key={i} style={g.v != null
            ? { position: 'absolute', left: `${g.v}%`, top: 0, bottom: 0, borderLeft: '1px dashed #F16A0D', zIndex: 25, pointerEvents: 'none' }
            : { position: 'absolute', top: `${g.h}%`, left: 0, right: 0, borderTop: '1px dashed #F16A0D', zIndex: 25, pointerEvents: 'none' }} />
        ))}
        {sel && <>
          {contorno(caixaSel, '#0d6efd')}
          {cantos(caixaSel, 'escala')}
          {/* Alça pra mexer só o preço (igual ao story) */}
          <div onPointerDown={(e) => apertar(e, { tipo: 'folha' })} title="Arraste pra mudar só o preço de lugar"
            style={{ position: 'absolute', zIndex: 30, left: `${caixaFolha.x + caixaFolha.w / 2}%`, top: `${caixaFolha.y + caixaFolha.h}%`,
              transform: 'translate(-50%, 6px)', background: '#F16A0D', color: '#fff', fontSize: 11, fontWeight: 600, borderRadius: 10,
              padding: '1px 8px', cursor: 'move', whiteSpace: 'nowrap', touchAction: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
            <i className="bi bi-arrows-move me-1"></i>preço
          </div>
          {sel === 'folha' && <>{contorno(caixaFolha, '#F16A0D')}{cantos(caixaFolha, 'folha-escala')}</>}
        </>}
      </div>
      <small className="text-muted d-block mt-1 text-center" style={{ fontSize: '0.74rem' }}>
        <i className="bi bi-arrows-move me-1"></i>Clique no produto: arraste e puxe os cantos · clique no preço pra mexer só nele · setas ajustam fino
      </small>
    </div>
  );
}

// Quadradinho de puxar (canto), igual ao do story.
const CURSOR_CANTO = { no: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', so: 'nesw-resize' };
function AlcaPoster({ canto, x, y, onPointerDown }) {
  return (
    <div onPointerDown={onPointerDown}
      style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)', width: 12, height: 12,
        background: '#fff', border: '2px solid #0d6efd', borderRadius: 3, cursor: CURSOR_CANTO[canto], zIndex: 30, touchAction: 'none' }} />
  );
}

// -----------------------------------------------------------------------------
// Painel de ajustes do pôster aberto
// -----------------------------------------------------------------------------
function Ajustes({ p, modelo, campanhaId, periodo, onMudar, onFoto, onRemover }) {
  const inputFoto = useRef(null);
  const bloco = caixaProduto(p, modelo, periodo);

  // Igual ao story: o produto inteiro em volta do centro dele; só a foto; só o preço.
  const tamanhoProduto = (w) => onMudar(q => {
    const c = caixaProduto(q, modelo, periodo);
    return { ...q, ...escalarPoster(q, w / c.w, c.x + c.w / 2, c.y + c.h / 2, modelo, periodo) };
  });
  const tamanhoFoto = (w) => onMudar(q => {
    const k = w / q.area.w, cx = q.area.x + q.area.w / 2, cy = q.area.y + q.area.h / 2;
    const nw = q.area.w * k, nh = q.area.h * k;
    return { ...q, area: { ...q.area, w: r1(nw), h: r1(nh), x: r1(cx - nw / 2), y: r1(cy - nh / 2) } };
  });
  // Cresce a partir do centro (nos dois sentidos) e não passa da página nem do período.
  const tamanhoFolha = (w) => onMudar(q => {
    const atual = folhaNaPagina(q.folha, q, modelo, periodo);
    const alt = (v) => v / modelo.folhaProp / PROPORCAO;
    const cx = atual.x + atual.w / 2, cy = atual.y + alt(atual.w) / 2;
    return { ...q, folha: folhaNaPagina({ w, x: cx - w / 2, y: cy - alt(w) / 2 }, q, modelo, periodo) };
  });

  const trocarFoto = async (file) => {
    if (!file) return;
    try {
      const { mime, dados } = await reduzirParaEnvio(file);
      await axios.post(`${API}/catalogo/campanha/${campanhaId}/foto/${p.codpro}`, { mime, dados });
      onFoto({ origem: 'propria', bruta: null });
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || e.message || 'Não consegui trocar a foto.', 'error');
    } finally {
      if (inputFoto.current) inputFoto.current.value = '';
    }
  };
  const voltarErp = async () => {
    try { await axios.delete(`${API}/catalogo/campanha/${campanhaId}/foto/${p.codpro}`); } catch { /* já não existia */ }
    onFoto({ origem: 'erp', bruta: null });
  };

  const rotulo = { fontSize: '0.8rem', fontWeight: 600 };
  return (
    <>
      <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Cód.{p.codpro}</div>
      <small className="text-muted d-block mb-2">De R$ {fmt(p.de)} · Por R$ {fmt(p.por)}</small>

      <label className="form-label mb-1" style={rotulo}>Modelo</label>
      <div className="d-flex gap-1 mb-1">
        {Object.entries(modelo.modelos).map(([nome, m]) => (
          <button key={nome} type="button" title={m.ajuda}
            className={`btn btn-sm flex-fill ${p.modelo === nome ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => onMudar(q => ({ ...q, ...posicoesDoModelo(modelo.modelos, nome), posDe: null }))}>
            {m.rotulo}
          </button>
        ))}
      </div>
      <small className="text-muted d-block mb-2" style={{ fontSize: '0.7rem' }}>Escolher o modelo volta foto, preço e "De:" pro lugar dele.</small>

      <label className="form-label mb-1" style={rotulo}>Texto do produto</label>
      <textarea className="form-control form-control-sm mb-2" rows={2} value={p.nome}
        onChange={e => onMudar(q => ({ ...q, nome: e.target.value }))} />

      <label className="form-label mb-0" style={rotulo}>Tamanho do produto <span className="text-muted">({Math.round(bloco.w)}%)</span></label>
      <input type="range" className="form-range" min="15" max="100" step="0.5" value={limitar(bloco.w, 15, 100)}
        onChange={e => tamanhoProduto(Number(e.target.value))} />
      <label className="form-label mb-0" style={rotulo}>Só a foto <span className="text-muted">({Math.round(p.area.w)}%)</span></label>
      <input type="range" className="form-range" min="10" max="150" step="0.5" value={limitar(p.area.w, 10, 150)}
        onChange={e => tamanhoFoto(Number(e.target.value))} />
      <label className="form-label mb-0" style={rotulo}>Só o preço <span className="text-muted">({Math.round(folhaNaPagina(p.folha, p, modelo, periodo).w)}%)</span></label>
      <input type="range" className="form-range" min="15" max="95" step="0.5" value={folhaNaPagina(p.folha, p, modelo, periodo).w}
        onChange={e => tamanhoFolha(Number(e.target.value))} />

      <label className="form-label mb-1" style={rotulo}>Foto encosta</label>
      <div className="d-flex gap-1 mb-2">
        {[['center top', 'em cima'], ['center center', 'no meio'], ['center bottom', 'embaixo']].map(([v, t]) => (
          <button key={v} type="button" className={`btn btn-sm flex-fill ${p.area.alinhar === v ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => onMudar(q => ({ ...q, area: { ...q.area, alinhar: v } }))}>{t}</button>
        ))}
      </div>

      <div className="form-check mb-2">
        <input className="form-check-input" type="checkbox" id={`de-${p.id}`} checked={!!p.mostrarDe}
          disabled={!(p.de > p.por)} onChange={e => onMudar(q => ({ ...q, mostrarDe: e.target.checked }))} />
        <label className="form-check-label" htmlFor={`de-${p.id}`} style={{ fontSize: '0.82rem' }}>
          Mostrar "De: R$ {fmt(p.de)}"
        </label>
        {p.posDe && p.mostrarDe && (
          <button type="button" className="btn btn-link btn-sm p-0 ms-2" style={{ fontSize: '0.74rem' }}
            onClick={() => onMudar(q => ({ ...q, posDe: null }))}>"De:" no lugar</button>
        )}
      </div>

      <label className="form-label mb-1" style={rotulo}>Estilo da foto</label>
      <div className="d-flex gap-1 flex-wrap mb-2">
        {ESTILOS.map(e => (
          <button key={e.valor} type="button"
            className={`btn btn-sm ${p.foto.estilo === e.valor ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => onFoto({ estilo: e.valor, tocouEstilo: true })}>{e.rotulo}</button>
        ))}
      </div>

      <div className="d-flex gap-2 flex-wrap mb-2">
        <input ref={inputFoto} type="file" accept="image/jpeg,image/png,image/webp" hidden
          onChange={e => trocarFoto(e.target.files?.[0])} />
        <button className="btn btn-sm btn-outline-primary" onClick={() => inputFoto.current?.click()}>
          <i className="bi bi-upload me-1"></i>Trocar foto
        </button>
        {p.foto.origem === 'propria' && (
          <button className="btn btn-sm btn-outline-secondary" onClick={voltarErp}>
            <i className="bi bi-arrow-counterclockwise me-1"></i>Voltar à do sistema
          </button>
        )}
      </div>
      <AvisoSugestaoFoto sugestao={p.sugestao} />

      <button className="btn btn-sm btn-outline-danger" onClick={onRemover}>
        <i className="bi bi-trash me-1"></i>Tirar este pôster
      </button>
    </>
  );
}

// -----------------------------------------------------------------------------
// Arte da campanha. "Fundo completo" é a página inteira já pronta (com o logo),
// em alta: com ela o topo e o fundo padrão saem. Topo e fundo trocam cada parte.
// -----------------------------------------------------------------------------
function ArteCampanha({ modelo, onTrocar, onVoltar }) {
  const inputs = { completo: useRef(null), topo: useRef(null), fundo: useRef(null) };
  const info = modelo.artes || {};
  const temCompleto = !!info.completo;

  // Quanto a arte rende impressa em A3 (ela ocupa a largura toda da página).
  const qualidade = (a, paginaInteira) => {
    if (!a?.largura) return null;
    const dpi = Math.round(a.largura / A3_POL);
    const nivel = dpi >= 250 ? ['ótima pra imprimir', '#198754'] : dpi >= 150 ? ['boa', '#A88A3A'] : ['vai sair suave na impressão', '#dc3545'];
    const prop = a.altura / a.largura;
    const foraA3 = paginaInteira && Math.abs(prop / PROPORCAO - 1) > 0.02;
    return (
      <small className="d-block" style={{ fontSize: '0.7rem', color: nivel[1] }}>
        {a.largura}×{a.altura}px · ≈{dpi} dpi em A3 — {nivel[0]}{a.bytes ? ` · ${(a.bytes / 1048576).toFixed(1)} MB` : ''}
        {foraA3 && <span className="d-block text-danger">Não está na proporção A3 em pé: as bordas vão ser cortadas pra caber.</span>}
      </small>
    );
  };

  const linha = (tipo, nome, ajuda, paginaInteira) => (
    <div className="mb-2" style={{ opacity: temCompleto && tipo !== 'completo' ? 0.5 : 1 }}>
      <div className="d-flex align-items-center justify-content-between">
        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{nome}</span>
        <span className="badge" style={{ background: info[tipo] ? '#19875422' : '#eee', color: info[tipo] ? '#198754' : '#777' }}>
          {info[tipo] ? 'da campanha' : (tipo === 'completo' ? 'não tem' : 'padrão')}
        </span>
      </div>
      <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>{ajuda}</small>
      {qualidade(info[tipo], paginaInteira)}
      <input ref={inputs[tipo]} type="file" accept="image/jpeg,image/png,image/webp" hidden
        onChange={e => { onTrocar(tipo, e.target.files?.[0]); e.target.value = ''; }} />
      <div className="d-flex gap-1 mt-1">
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.74rem' }} onClick={() => inputs[tipo].current?.click()}>
          <i className="bi bi-image me-1"></i>{info[tipo] ? 'Trocar' : 'Subir'}
        </button>
        {info[tipo] && (
          <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: '0.74rem' }} onClick={() => onVoltar(tipo)}>
            {tipo === 'completo' ? 'Tirar' : 'Voltar à padrão'}
          </button>
        )}
      </div>
    </div>
  );
  return (
    <>
      <div className="fw-bold mb-1" style={{ fontSize: '0.85rem' }}>Arte da campanha <small className="text-muted fw-normal">(vale pra todos os pôsteres)</small></div>
      {linha('completo', 'Fundo completo', 'A página inteira já pronta, com o logo — em alta (ideal: 3508×4961 px, A3 a 300 dpi). Com ela, topo e fundo abaixo não entram. Se a arte já traz a data, deixe o Período em branco.', true)}
      {temCompleto && <small className="d-block mb-2 text-muted" style={{ fontSize: '0.7rem' }}>Usando o fundo completo — topo e fundo abaixo ficam de lado enquanto ele estiver aqui.</small>}
      {linha('topo', 'Topo', 'PNG com fundo transparente, na largura da página. A padrão é o recorte do exemplo (≈100 dpi): pra imprimir mais nítido, suba a arte original.', false)}
      {linha('fundo', 'Fundo', 'Imagem em pé (proporção A3). A padrão é o fundo econômico em tinta preta, em vetor (nítido em qualquer tamanho).', true)}
    </>
  );
}
